import { useEffect, useRef } from "react"

export interface InfiniteScrollProps {
  onLoadMore: () => void
  disabled?: boolean
  /** Text shown while the next page is loading. */
  loading?: boolean
}

/**
 * Sentinel based infinite scroll: the callback fires when the sentinel becomes
 * visible, which also covers keyboard-only scrolling.
 */
export function InfiniteScroll({ onLoadMore, disabled, loading }: InfiniteScrollProps) {
  const ref = useRef<HTMLDivElement>(null)
  const callback = useRef(onLoadMore)
  callback.current = onLoadMore

  useEffect(() => {
    const element = ref.current
    if (!element || disabled) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callback.current()
      },
      { rootMargin: "600px 0px" },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [disabled])

  return (
    <div ref={ref} className="infinite-sentinel" aria-hidden={!loading}>
      {loading ? <span className="infinite-loading">Loading more…</span> : null}
    </div>
  )
}
